import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  DirectionsRenderer,
} from "@react-google-maps/api";
import config from "../../config.js";
import { CircularProgress, Typography } from "@mui/material";
import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import ScreenRouteVehicleStatusTable from "./ScreenRouteVehicleStatusTable";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import { useTranslation } from "react-i18next";
import {
  getVehicleDirection,
  updateVehicleDirection,
} from "../../components/dashboard/dashboardAPI.js";
import { checkDirectionChange } from "../vehicle/vehicleTrack/mapUtils";
import { calculateBearing, calculateDistance } from "../../utils/calculateDistance";

const styles = {
  container: {
    padding: "32px",
  },
  cardSection: {
    marginBottom: "32px",
  },
  card: {
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px",
    color: "white",
  },
  vehicleCard: {
    backgroundColor: "rgba(88, 0, 191, 1)",
  },
  stopCard: {
    backgroundColor: "rgba(0, 178, 191, 1)",
  },
  scheduleCard: {
    backgroundColor: "rgba(191, 80, 0, 1)",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
  },
  icon: {
    fontSize: "40px",
    marginTop: "10px",
  },
  mapContainer: {
    width: "100%",
    height: "100vh",
    marginBottom: "32px",
  },
  tableContainer: {
    marginTop: "32px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "-7px",
  },
  tableHeader: {
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#f5f5f5",
  },
  tableCell: {
    border: "1px solid #ddd",
    padding: "8px",
  },
  noData: {
    textAlign: "center",
    padding: "16px",
  },
  formHeader: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: "16px",
  },
};

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const libraries = ["geometry"];

const normalizeDeviceData = (data) => {
  const deviceId = data?.deviceId || data?.device_id;
  const latitude = data?.latitude ?? data?.Latitude;
  const longitude = data?.longitude ?? data?.Longitude;

  return {
    ...data,
    deviceId,
    latitude,
    longitude,
    speed: data?.speed ?? data?.Speed ?? 0,
    timestamp: data?.timestamp || data?.createdAt || new Date().toISOString(),
    rawData: data?.rawData || data?.RawData || "",
  };
};

const TRIP_SPEED_KMH = 30;
const DIRECTION_SAMPLE_COUNT = 8;

const pathPointDistanceKm = (first, second) =>
  window.google.maps.geometry.spherical.computeDistanceBetween(first, second) / 1000;

const distanceAlongPath = (path, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return 0;
  const step = fromIndex < toIndex ? 1 : -1;
  let total = 0;
  for (let index = fromIndex; index !== toIndex; index += step) {
    const segment = pathPointDistanceKm(path[index], path[index + step]);
    if (Number.isFinite(segment)) total += segment;
  }
  return total;
};

const closestPathIndex = (path, vehiclePoint, previousIndex) => {
  let nearestIndex = 0;
  let nearestDistance = Infinity;
  path.forEach((point, index) => {
    const distance = pathPointDistanceKm(vehiclePoint, point);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });
  if (previousIndex == null) return nearestIndex;

  let continuedIndex = previousIndex;
  let continuedDistance = Infinity;
  const start = Math.max(0, previousIndex - 25);
  const end = Math.min(path.length - 1, previousIndex + 25);
  for (let index = start; index <= end; index += 1) {
    const distance = pathPointDistanceKm(vehiclePoint, path[index]);
    if (distance < continuedDistance) {
      continuedDistance = distance;
      continuedIndex = index;
    }
  }
  return continuedDistance < nearestDistance + 0.08 ? continuedIndex : nearestIndex;
};

const calculateDistanceAndETA = (
  vehiclePos,
  routePath,
  vehicleDirection = "source_to_dest",
  previousIndex = null,
) => {
  if (!routePath || routePath.length === 0) return { distance: null, eta: null, closestIndex: null };

  const vehiclePoint = new window.google.maps.LatLng(vehiclePos.lat, vehiclePos.lng);
  const closestIndex = closestPathIndex(routePath, vehiclePoint, previousIndex);
  const targetIndex = vehicleDirection === "dest_to_source" ? 0 : routePath.length - 1;
  const totalDistance = distanceAlongPath(routePath, closestIndex, targetIndex);

  if (!Number.isFinite(totalDistance)) return { distance: null, eta: null, closestIndex };
  const eta = Math.round((totalDistance / TRIP_SPEED_KMH) * 3600);
  return {
    distance: Number(totalDistance.toFixed(1)),
    eta: Number.isFinite(eta) ? eta : null,
    closestIndex,
  };
};

const RouteScreen = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { routeId } = useParams();
  const routeData = location.state?.selectedRouteData;
  const [vehicleData, setVehicleData] = useState({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [directions, setDirections] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const routePathRef = useRef(null);
  const autoFitDoneRef = useRef(false);
  const previousPositionsRef = useRef({});
  const vehicleHeadingsRef = useRef({});
  const stableDirectionRef = useRef({});
  const tripProgressRef = useRef({});
  const positionHistoryRef = useRef({});
  const [mapError, setMapError] = useState(null);
  const [labelPositions, setLabelPositions] = useState({});
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleName, setVehicleName] = useState({});
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [vehicleHeadings, setVehicleHeadings] = useState({});
  const eventSourceRef = useRef(null);
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [EtaAndDistance, setEtaAndDistance] = useState([]);

  const { settings } = useSettings();
  const {
    getOriginStopIcon,
    getDestinationStopIcon,
    getIntermediateStopIcon,
    getVehicleIcon: getCustomVehicleIcon,
  } =
    useMapIcons(
      settings,
      routeData?.vehicles || [],
      routeData?.stops || [],
      routeData ? [routeData] : [],
    );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey || "YOUR_API_KEY_HERE",
    libraries,
  });

  const fetchAllVehicleLocations = useCallback(() => {
    if (vehicleIds.length === 0) return;

    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const source = new EventSource(sseUrl);
    eventSourceRef.current = source;

    source.onopen = () => {
      console.log("Persistent SSE connection established");
    };

    source.onerror = (error) => {
      console.error("Persistent SSE error:", error);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setTimeout(fetchAllVehicleLocations, 5000);
    };

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type && data.type === "connected") {
          console.log("Received connection confirmation:", data);
          return;
        }

        const payloads = Array.isArray(data)
          ? data
          : Array.isArray(data.locations)
            ? data.locations
            : [data];
        const newLocations = payloads
          .map(normalizeDeviceData)
          .filter(
            (location) =>
              location.deviceId &&
              location.latitude != null &&
              location.longitude != null,
          );

        if (newLocations.length === 0) {
          console.warn("Received SSE data in unexpected format:", data);
          return;
        }

        setVehicleLocations((previousLocations) => {
          const locationsByDeviceId = new Map(
            previousLocations.map((location) => [String(location.deviceId), location]),
          );
          newLocations.forEach((location) => {
            locationsByDeviceId.set(String(location.deviceId), location);
          });
          return Array.from(locationsByDeviceId.values());
        });
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length > 0) {
      fetchAllVehicleLocations();
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }
  }, [vehicleIds, fetchAllVehicleLocations]);

  useEffect(() => {
    if (vehicleIds.length === 0) return undefined;

    let cancelled = false;
    const loadVehicleDirections = async () => {
      const entries = await Promise.all(
        vehicleIds.map(async (deviceId) => [
          deviceId,
          await getVehicleDirection(deviceId),
        ]),
      );
      if (cancelled) return;
      setVehicleDirections((current) => {
        const next = { ...current };
        entries.forEach(([deviceId, direction]) => {
          if (
            !stableDirectionRef.current[String(deviceId)] &&
            (direction === "source_to_dest" || direction === "dest_to_source")
          ) {
            next[deviceId] = direction;
          }
        });
        return next;
      });
    };

    loadVehicleDirections();
    const intervalId = window.setInterval(loadVehicleDirections, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length === 0) return undefined;

    let cancelled = false;
    const fetchInitialLocations = async () => {
      try {
        const locations = await Promise.all(
          vehicleIds.map(async (deviceId) => {
            const response = await fetch(
              `${config.baseURL}/location/latest/${encodeURIComponent(deviceId)}`,
              { credentials: "include" },
            );
            if (!response.ok) return null;
            const data = await response.json();
            if (!data) return null;
            const normalized = normalizeDeviceData(data);
            return normalized.deviceId &&
              normalized.latitude != null &&
              normalized.longitude != null
              ? normalized
              : null;
          }),
        );

        if (!cancelled) {
          setVehicleLocations(locations.filter(Boolean));
        }
      } catch (initialLocationError) {
        console.error("Unable to load initial route vehicle locations", initialLocationError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitialLocations();
    return () => {
      cancelled = true;
    };
  }, [vehicleIds]);

  const getVehicleLabelStyle = (vehicle) => {
    const deviceId = String(vehicle?.deviceId || vehicle?.device_id || "");
    const assigned = (routeData?.vehicles || []).find(
      (item) => String(item?.device_id || item?.deviceId || "") === deviceId,
    );
    const status = String(
      vehicle?.status || vehicle?.Status || assigned?.status || "ACTIVE",
    )
      .trim()
      .toUpperCase();
    const inactive = status === "INACTIVE" || status === "DEAD";
    return {
      backgroundColor: inactive ? "#F9E719" : "#008C23",
      color: inactive ? "#000" : "#fff",
    };
  };

  const center = useMemo(() => {
    if (routeData?.routeCoordinates?.[0]) {
      return {
        lat: parseFloat(routeData.routeCoordinates[0].latitude),
        lng: parseFloat(routeData.routeCoordinates[0].longitude),
      };
    }
    if (routeData?.stops?.[0]) {
      return {
        lat: parseFloat(routeData.stops[0].latitude),
        lng: parseFloat(routeData.stops[0].longitude),
      };
    }
    return { lat: 0, lng: 0 };
  }, [routeData]);

  const stopMarkers = useMemo(() => {
    return routeData
      ? routeData.stops.map((stop) => ({
          position: {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          },
          label: stop.name,
        }))
      : [];
  }, [routeData]);

  const calculateRouteDirections = useCallback(() => {
    if (!window.google || !routeData?.routeCoordinates?.length || !mapInstance)
      return;
    const directionsService = new window.google.maps.DirectionsService();
    const origin = new window.google.maps.LatLng(
      parseFloat(routeData.routeCoordinates[0].latitude),
      parseFloat(routeData.routeCoordinates[0].longitude),
    );
    const destination = new window.google.maps.LatLng(
      parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .latitude,
      ),
      parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .longitude,
      ),
    );
    const waypoints = routeData.routeCoordinates.slice(1, -1).map((coord) => ({
      location: new window.google.maps.LatLng(
        parseFloat(coord.latitude),
        parseFloat(coord.longitude),
      ),
      stopover: coord.isStop || false,
    }));
    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          console.log("Directions received:", result);
          setDirections({ ...result, routeColor: routeData.routeColor });
          routePathRef.current = result.routes[0].overview_path;
        } else {
          console.error(
            `Error fetching directions for route ${routeId}: ${status}`,
          );
          setError(`Error fetching directions: ${status}`);
        }
      },
    );
  }, [routeData, mapInstance, routeId]);

  const onLoad = useCallback((map) => {
    setMapLoaded(true);
    setMapInstance(map);
  }, []);

  const formatTime = (seconds) => {
    const total = Number(seconds);
    if (!Number.isFinite(total)) return t("N/A");
    if (total <= 0) return t("Arrived");
    const rounded = Math.round(total);
    const hours = Math.floor(rounded / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const secs = rounded % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDistance = (distance) => {
    const value = Number(distance);
    if (!Number.isFinite(value) || value < 0) return t("N/A");
    return `${value.toFixed(1)} km`;
  };

  const calculateLabelPositions = useCallback((vehicles) => {
    const positions = {};
    const LABEL_HEIGHT = 30;
    const groupedVehicles = [];
    vehicles.forEach((vehicle) => {
      if (!vehicle || !vehicle.position) return;
      let foundGroup = false;
      for (const group of groupedVehicles) {
        const firstVehicle = group[0];
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(
              vehicle.position.lat,
              vehicle.position.lng,
            ),
            new window.google.maps.LatLng(
              firstVehicle.position.lat,
              firstVehicle.position.lng,
            ),
          );
        if (distance < 50) {
          group.push(vehicle);
          foundGroup = true;
          break;
        }
      }
      if (!foundGroup) groupedVehicles.push([vehicle]);
    });
    groupedVehicles.forEach((group) => {
      group.forEach((vehicle, index) => {
        positions[vehicle.device_id] = LABEL_HEIGHT * index;
      });
    });
    return positions;
  }, []);

  useEffect(() => {
    if (routeData?.vehicles?.length > 0) {
      const ids = [
        ...new Set(
          routeData.vehicles
            .map((vehicle) => vehicle?.device_id)
            .filter(Boolean)
            .map(String),
        ),
      ];
      console.log("Vehicle IDs:", ids);
      setVehicleIds(ids);
    } else {
      setVehicleIds([]);
      setLoading(false);
    }
  }, [routeData]);

  useEffect(() => {
    const directionUpdates = {};
    const updatedVehicleData = vehicleLocations.reduce((acc, location) => {
      if (
        location.deviceId &&
        location.latitude != null &&
        location.longitude != null
      ) {
        const position = {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        };
        const deviceKey = String(location.deviceId);
        const reportedDirection =
          vehicleDirections[deviceKey] || vehicleDirections[location.deviceId];
        if (
          !stableDirectionRef.current[deviceKey] &&
          (reportedDirection === "source_to_dest" ||
            reportedDirection === "dest_to_source")
        ) {
          stableDirectionRef.current[deviceKey] = reportedDirection;
        }
        const history = [
          ...(positionHistoryRef.current[deviceKey] || []),
          position,
        ].slice(-DIRECTION_SAMPLE_COUNT);
        positionHistoryRef.current[deviceKey] = history;
        const detectedDirection = checkDirectionChange(
          history,
          routeData?.stops || [],
        );
        if (
          detectedDirection &&
          detectedDirection !== stableDirectionRef.current[deviceKey]
        ) {
          stableDirectionRef.current[deviceKey] = detectedDirection;
          directionUpdates[deviceKey] = detectedDirection;
          updateVehicleDirection(location.deviceId, detectedDirection).catch(
            (directionError) => {
              console.error("Failed to update direction:", directionError);
            },
          );
        }
        const direction = stableDirectionRef.current[deviceKey] || "source_to_dest";
        const previousTrip = tripProgressRef.current[deviceKey];
        const measured = calculateDistanceAndETA(
          position,
          routePathRef.current,
          direction,
          previousTrip?.direction === direction ? previousTrip.index : null,
        );
        let distance = measured.distance;
        let eta = measured.eta;
        if (
          previousTrip &&
          previousTrip.direction === direction &&
          Number.isFinite(measured.distance) &&
          Number.isFinite(previousTrip.distance)
        ) {
          const samePlace = Math.abs(measured.distance - previousTrip.distance) < 0.1;
          const upwardSpike = measured.distance > previousTrip.distance + 0.3;
          if (samePlace || upwardSpike) {
            distance = previousTrip.distance;
            eta = previousTrip.eta;
          }
        }
        tripProgressRef.current[deviceKey] = {
          direction,
          distance,
          eta,
          index: measured.closestIndex,
        };
        acc[location.deviceId] = {
          device_id: location.deviceId,
          position,
          timestamp: location.timestamp,
          distance,
          eta,
        };
      }
      return acc;
    }, {});
    let headingsChanged = false;
    vehicleLocations.forEach((location) => {
      const id = String(location.deviceId || location.device_id || "");
      const lat = parseFloat(location.latitude ?? location.Latitude);
      const lng = parseFloat(location.longitude ?? location.Longitude);
      if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const current = { lat, lng };
      const previous = previousPositionsRef.current[id];
      if (previous) {
        const moved = calculateDistance(previous, current);
        if (moved > 5) {
          const bearing = calculateBearing(
            { latitude: previous.lat, longitude: previous.lng },
            { latitude: current.lat, longitude: current.lng },
          );
          if (vehicleHeadingsRef.current[id] !== bearing) {
            vehicleHeadingsRef.current[id] = bearing;
            headingsChanged = true;
          }
        }
      } else if (Number.isFinite(Number(location.heading))) {
        vehicleHeadingsRef.current[id] = Number(location.heading);
        headingsChanged = true;
      }
      previousPositionsRef.current[id] = current;
    });
    if (headingsChanged) setVehicleHeadings({ ...vehicleHeadingsRef.current });
    if (Object.keys(directionUpdates).length > 0) {
      setVehicleDirections((current) => ({ ...current, ...directionUpdates }));
    }

    setVehicleData(updatedVehicleData);
    setLastUpdateTime(new Date().toLocaleString());
    setLoading(false);
    if (Object.values(updatedVehicleData).length > 0) {
      const newPositions = calculateLabelPositions(
        Object.values(updatedVehicleData),
      );
      setLabelPositions(newPositions);
    }
  }, [
    vehicleLocations,
    routeData?.stops,
    vehicleDirections,
    calculateLabelPositions,
  ]);

  useEffect(() => {
    if (isLoaded && routeData?.routeCoordinates?.length > 0 && mapInstance) {
      autoFitDoneRef.current = false;
      calculateRouteDirections();
    }
  }, [isLoaded, routeData, mapInstance, calculateRouteDirections]);

  useEffect(() => {
    if (
      !mapInstance ||
      !directions?.routes?.[0]?.overview_path?.length ||
      autoFitDoneRef.current
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    directions.routes[0].overview_path.forEach((point) => bounds.extend(point));
    vehicleLocations.forEach((vehicle) => {
      const latitude = Number(vehicle.latitude ?? vehicle.Latitude);
      const longitude = Number(vehicle.longitude ?? vehicle.Longitude);
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        bounds.extend({ lat: latitude, lng: longitude });
      }
    });
    mapInstance.fitBounds(bounds, 60);
    autoFitDoneRef.current = true;
  }, [directions, mapInstance, vehicleLocations]);

  useEffect(() => {
    const vehicleName = {};
    if (routeData?.vehicles?.length > 0) {
      routeData?.vehicles?.forEach((v) => {
        const deviceId = v?.device_id;
        if (deviceId && !vehicleName[deviceId]) {
          vehicleName[deviceId] = v?.name;
        }
      });
      setVehicleName(vehicleName);
    }
  }, [routeData]);

  if (!routeData) {
    return (
      <div style={styles.noData}>
        <Typography variant="h6">{t("No route data available")}</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.noData}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.noData}>
        <Typography color="error">{t("Failed to load Google Maps")}</Typography>
        <Typography color="error">{mapError}</Typography>
        <button onClick={() => window.location.reload()}>{t("Refresh")}</button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={styles.noData}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        width: "100vw",
        padding: "16px",
        height: "100vh",
      }}
    >
      <div style={{ width: "71%" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "95.5vh" }}
          center={center}
          zoom={18}
          onLoad={onLoad}
          options={{
            styles: mapStyles,
            disableDefaultUI: true,
            zoomControl: false,
            streetViewControl: true,
            fullscreenControl: true,
            mapTypeControl: false,
            gestureHandling: "greedy",
            minZoom: 15,
            maxZoom: 60,
          }}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: directions.routeColor || "#0000FF",
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                },
              }}
            />
          )}
          {stopMarkers.map((marker, index) => (
            <React.Fragment key={index}>
              <OverlayView
                position={marker.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    position: "relative",
                    top: index % 2 === 0 ? "-96px" : "-132px",
                    left: "0px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      width: "2px",
                      backgroundColor:
                        index === 0
                          ? "green"
                          : index === stopMarkers.length - 1
                            ? "blue"
                            : "#183059",
                      height: index % 2 === 0 ? "62px" : "98px",
                      top: "10px",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "white",
                      color:
                        index === 0
                          ? "green"
                          : index === stopMarkers.length - 1
                            ? "blue"
                            : "black",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "600",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {marker.label}
                  </div>
                </div>
              </OverlayView>
              <Marker
                position={marker.position}
                icon={(() => {
                  const isOrigin = index === 0;
                  const isDestination = index === stopMarkers.length - 1;
                  let customIcon = null;
                  if (isOrigin) customIcon = getOriginStopIcon();
                  else if (isDestination) customIcon = getDestinationStopIcon();
                  else customIcon = getIntermediateStopIcon();
                  return (
                    customIcon ||
                    (isOrigin
                      ? {
                          url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                          scaledSize: new window.google.maps.Size(40, 40),
                        }
                      : isDestination
                        ? {
                            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                            scaledSize: new window.google.maps.Size(40, 40),
                          }
                        : undefined)
                  );
                })()}
              />
            </React.Fragment>
          ))}
          {Array.isArray(vehicleLocations) &&
            vehicleLocations.map(
              (vehicle) =>
                vehicle &&
                (vehicle.latitude ?? vehicle.Latitude) != null &&
                (vehicle.longitude ?? vehicle.Longitude) != null && (
                  <React.Fragment key={vehicle.deviceId || vehicle.device_id}>
                    <OverlayView
                      position={{
                        lat: parseFloat(vehicle.latitude ?? vehicle.Latitude),
                        lng: parseFloat(vehicle.longitude ?? vehicle.Longitude),
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          transform: "translate(-50%, calc(-100% - 20px))",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            ...getVehicleLabelStyle(vehicle),
                            fontSize: "12px",
                            fontWeight: 700,
                            fontFamily: "Arial, sans-serif",
                            lineHeight: "14px",
                            padding: "4px 8px",
                            whiteSpace: "nowrap",
                            borderRadius: "8px",
                            border: "1px solid #111",
                          }}
                        >
                          {vehicleName[vehicle.deviceId] ||
                            vehicleName[String(vehicle.deviceId)] ||
                            vehicleName[vehicle.device_id] ||
                            vehicleName[String(vehicle.device_id)] ||
                            "---"}
                        </div>
                        <div
                          style={{
                            width: "2px",
                            height: `${
                              28 +
                              (labelPositions[
                                vehicle.deviceId || vehicle.device_id
                              ] || 0)
                            }px`,
                            backgroundColor: getVehicleLabelStyle(vehicle).backgroundColor,
                          }}
                        />
                      </div>
                    </OverlayView>
                    <Marker
                      position={{
                        lat: parseFloat(vehicle.latitude ?? vehicle.Latitude),
                        lng: parseFloat(vehicle.longitude ?? vehicle.Longitude),
                      }}
                      optimized={false}
                      icon={
                        getCustomVehicleIcon(
                          vehicle.deviceId || vehicle.device_id,
                          vehicleHeadings[
                            String(vehicle.deviceId || vehicle.device_id)
                          ] ??
                            (Number.isFinite(Number(vehicle.heading))
                              ? Number(vehicle.heading)
                              : 0),
                          vehicleDirections[
                            vehicle.deviceId || vehicle.device_id
                          ] || "source_to_dest",
                          mapInstance?.getZoom(),
                        ) || {
                          url: BusIcon,
                          scaledSize: new window.google.maps.Size(30, 30),
                          anchor: new window.google.maps.Point(15, 15),
                        }
                      }
                    />
                  </React.Fragment>
                ),
            )}
        </GoogleMap>
      </div>
      <div style={{ width: "28%" }}>
        <ScreenRouteVehicleStatusTable
          loading={loading}
          error={error}
          vehicleData={vehicleData}
          formatDistance={formatDistance}
          formatTime={formatTime}
          vehicleLocations={vehicleLocations}
          vehicleName={vehicleName}
        />
      </div>
    </div>
  );
};

export default RouteScreen;
